import { Sheet, VisuallyHidden } from '@silk-hq/components';
import React from 'react';
import { cn } from '../../lib/classNameHelpers';

// ================================================================================================
// Root
// ================================================================================================

type SheetRootProps = React.ComponentPropsWithoutRef<typeof Sheet.Root>;
type SidebarRootProps = Omit<SheetRootProps, 'license'> & {
  license?: SheetRootProps['license'];
};

function SidebarRoot({ children, ...restProps }: SidebarRootProps) {
  return (
    <Sheet.Root license="commercial" sheetRole="dialog" {...restProps}>
      {children}
    </Sheet.Root>
  );
}

// ================================================================================================
// View
// ================================================================================================

function SidebarView({
  children,
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.View>) {
  return (
    <Sheet.View
      className={cn('z-50 h-[calc(var(--silk-100-lvh-dvh-pct)+60px)]', className)}
      contentPlacement="left"
      swipeOvershoot={false}
      nativeEdgeSwipePrevention={true}
      {...restProps}
    >
      {children}
    </Sheet.View>
  );
}

// ================================================================================================
// Backdrop
// ================================================================================================

function SidebarBackdrop({
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.Backdrop>) {
  return <Sheet.Backdrop className={cn(className)} {...restProps} />;
}

// ================================================================================================
// Content
// ================================================================================================

function SidebarContent({
  children,
  className,
  ...restProps
}: React.ComponentPropsWithoutRef<typeof Sheet.Content>) {
  return (
    <Sheet.Content
      className={cn('box-border w-[min(90vw,325px)] bg-backgroundPrimary shadow-lg', className)}
      {...restProps}
    >
      <VisuallyHidden.Root>
        <Sheet.Title>Sidebar Example</Sheet.Title>
        <Sheet.Trigger action="dismiss">Close Sidebar example</Sheet.Trigger>
      </VisuallyHidden.Root>
      {children}
    </Sheet.Content>
  );
}

export { SidebarRoot, SidebarView, SidebarBackdrop, SidebarContent };
export const SidebarPortal = Sheet.Portal;
export const SidebarTrigger = Sheet.Trigger;
export const SidebarHandle = Sheet.Handle;
export const SidebarOutlet = Sheet.Outlet;
export const SidebarTitle = Sheet.Title;
export const SidebarDescription = Sheet.Description;
